/**
 * Processes list toolbar: a search `Input` plus two filter `Select`s (type +
 * status). Purely presentational — all state lives in the parent list section.
 */
import { Search } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vnetwork/ui/primitives';
import type { ProcessStatus, ProcessType } from '@/api/capacities';
import { PROCESS_STATUS_LABEL, PROCESS_TYPE_LABEL } from './process-format';

const ANY = '__any__';

const TYPE_OPTIONS = Object.entries(PROCESS_TYPE_LABEL) as [ProcessType, string][];
const STATUS_OPTIONS = Object.entries(PROCESS_STATUS_LABEL) as [ProcessStatus, string][];

export interface ProcessListToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  type?: ProcessType;
  onTypeChange: (value?: ProcessType) => void;
  status?: ProcessStatus;
  onStatusChange: (value?: ProcessStatus) => void;
}

export function ProcessListToolbar({
  query,
  onQueryChange,
  type,
  onTypeChange,
  status,
  onStatusChange,
}: ProcessListToolbarProps) {
  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search processes…"
          startSlot={<Search />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          <Select
            value={type ?? ANY}
            onValueChange={(v) => onTypeChange(v === ANY ? undefined : (v as ProcessType))}
          >
            <SelectTrigger style={{ minWidth: 180 }}>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All types</SelectItem>
              {TYPE_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={status ?? ANY}
            onValueChange={(v) => onStatusChange(v === ANY ? undefined : (v as ProcessStatus))}
          >
            <SelectTrigger style={{ minWidth: 170 }}>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All statuses</SelectItem>
              {STATUS_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      }
    />
  );
}
